/**
 * Minuteur du panier Valcourt (remplace celui de Kaching Cart).
 * - Démarre à `duration` secondes (10 min par défaut) pour chaque nouveau panier.
 * - S'arrête à 00:00 sans vider le panier.
 * - Repart de zéro dès qu'un produit est ajouté (le nombre d'articles augmente).
 * - Quand le panier est vidé, le minuteur est oublié : le prochain panier repart à 10:00.
 * - Un minuteur terminé depuis plus d'une heure repart à la visite suivante.
 */
const END_KEY = 'vc_cart_timer_end';
const COUNT_KEY = 'vc_cart_timer_count';
const STALE_AFTER = 60 * 60 * 1000;

const store = {
  get(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  },
  set(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* stockage indisponible */ }
  },
  remove(key) {
    try { localStorage.removeItem(key); } catch (e) { /* stockage indisponible */ }
  },
};

function resetTimerStorage() {
  store.remove(END_KEY);
  store.set(COUNT_KEY, '0');
}

/* Panier vidé (le minuteur n'est alors plus affiché) : on repart de zéro au prochain ajout. */
document.addEventListener('cart:update', (event) => {
  const detail = /** @type {any} */ (event).detail || {};
  const count = detail.data?.itemCount ?? detail.resource?.item_count;
  if (count === 0) resetTimerStorage();
});

class CartCountdownTimer extends HTMLElement {
  static observedAttributes = ['data-item-count'];

  connectedCallback() {
    this.valueEl = this.querySelector('[data-countdown-value]');
    this.sync();
    this.tick();
    if (!this.interval) this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    this.interval = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;
    this.sync();
    this.tick();
  }

  get duration() {
    return parseInt(this.getAttribute('duration') || '600', 10) * 1000;
  }

  sync() {
    const now = Date.now();
    const count = parseInt(this.dataset.itemCount || '0', 10);
    const previousCount = parseInt(store.get(COUNT_KEY) ?? '0', 10);
    let end = Number(store.get(END_KEY));

    if (count <= 0) {
      resetTimerStorage();
      this.endTime = now + this.duration;
      return;
    }

    const missing = !end || Number.isNaN(end);
    const stale = !missing && end + STALE_AFTER < now;
    const productAdded = count > previousCount;

    if (missing || stale || productAdded) {
      end = now + this.duration;
      store.set(END_KEY, String(end));
    }

    store.set(COUNT_KEY, String(count));
    this.endTime = end;
  }

  tick() {
    const remaining = Math.max(0, (this.endTime || 0) - Date.now());
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    if (this.valueEl) {
      this.valueEl.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  }
}

if (!customElements.get('cart-countdown-timer')) {
  customElements.define('cart-countdown-timer', CartCountdownTimer);
}
